// Live API (https://sentientdigital.in → Render backend). Override anytime with REACT_APP_API_URL at build time.
const PRODUCTION_API_BASE = 'https://sdbackend-8ywd.onrender.com/api';

// Default `/api`: CRA dev server proxies via src/setupProxy.js. Override with REACT_APP_API_URL for prod builds.
function normalizeApiBase() {
  const raw = process.env.REACT_APP_API_URL;
  if (raw != null && String(raw).trim() !== '') {
    let base = String(raw).trim().replace(/\/+$/, '');
    if (!base.endsWith('/api')) base = `${base}/api`;
    return base;
  }
  if (typeof window !== 'undefined' && window.location.protocol === 'file:') {
    return 'http://127.0.0.1:5000/api';
  }
  if (process.env.NODE_ENV === 'production') {
    return PRODUCTION_API_BASE;
  }
  return '/api';
}

const API_BASE = normalizeApiBase();

// Get stored token
const getToken = () => localStorage.getItem('sd_admin_token');

async function parseJsonBody(response) {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    throw new Error(
      'Invalid response from server. Check REACT_APP_API_URL and that the API is running.'
    );
  }
}

// Generic fetch wrapper
const apiFetch = async (endpoint, options = {}) => {
  const token = getToken();
  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers
    },
    ...options
  };

  // Don't set Content-Type for FormData
  if (options.body instanceof FormData) {
    delete config.headers['Content-Type'];
  }

  let response;
  try {
    response = await fetch(`${API_BASE}${endpoint}`, config);
  } catch (e) {
    const msg =
      e && e.message === 'Failed to fetch'
        ? 'Network error — API unreachable or blocked (CORS / wrong URL / HTTP vs HTTPS).'
        : e.message;
    throw new Error(msg);
  }
  const data = await parseJsonBody(response);

  if (!response.ok) {
    throw new Error(data.message || 'Something went wrong');
  }
  return data;
};

// ==================== AUTH ====================
export const authAPI = {
  login: (email, password) =>
    apiFetch('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  getMe: () => apiFetch('/auth/me'),
  register: (userData) =>
    apiFetch('/auth/register', { method: 'POST', body: JSON.stringify(userData) }),
  logout: () => {
    localStorage.removeItem('sd_admin_token');
    localStorage.removeItem('sd_admin_user');
  },
  isLoggedIn: () => !!getToken(),
  setToken: (token, user) => {
    localStorage.setItem('sd_admin_token', token);
    localStorage.setItem('sd_admin_user', JSON.stringify(user));
  },
  getUser: () => {
    try { return JSON.parse(localStorage.getItem('sd_admin_user')); } catch { return null; }
  }
};

// ==================== BLOGS ====================
export const blogAPI = {
  getPublished: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiFetch(`/blogs${query ? '?' + query : ''}`);
  },
  getFeatured: () => apiFetch('/blogs/featured'),
  getById: (id) => apiFetch(`/blogs/${id}`),
  getAll: () => apiFetch('/blogs/admin/all'),
  create: (blogData) =>
    apiFetch('/blogs', { method: 'POST', body: JSON.stringify(blogData) }),
  update: (id, blogData) =>
    apiFetch(`/blogs/${id}`, { method: 'PUT', body: JSON.stringify(blogData) }),
  delete: (id) =>
    apiFetch(`/blogs/${id}`, { method: 'DELETE' }),
  changeStatus: (id, status) =>
    apiFetch(`/blogs/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),
  uploadImage: (formData, id = null) => {
    const endpoint = id ? `/blogs/${id}/image` : '/blogs/upload-image';
    return apiFetch(endpoint, { method: 'POST', body: formData });
  },
  /** Admin: Bytez-powered draft (title, content, excerpt, tags, category, image URL). */
  generateAiDraft: (topic) =>
    apiFetch('/blogs/admin/ai-draft', { method: 'POST', body: JSON.stringify({ topic }) })
};

// ==================== CONTACTS ====================
export const contactAPI = {
  submit: (formData) =>
    apiFetch('/contacts', { method: 'POST', body: JSON.stringify(formData) }),
  getAll: () => apiFetch('/contacts'),
  getById: (id) => apiFetch(`/contacts/${id}`),
  update: (id, data) =>
    apiFetch(`/contacts/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id) =>
    apiFetch(`/contacts/${id}`, { method: 'DELETE' })
};

// ==================== LEADS ====================
export const leadAPI = {
  submit: (formData) =>
    apiFetch('/leads', { method: 'POST', body: JSON.stringify(formData) }),
  getAll: () => apiFetch('/leads'),
  update: (id, data) =>
    apiFetch(`/leads/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id) =>
    apiFetch(`/leads/${id}`, { method: 'DELETE' })
};

// ==================== NEWSLETTER ====================
export const newsletterAPI = {
  subscribe: (email) =>
    apiFetch('/newsletter/subscribe', { method: 'POST', body: JSON.stringify({ email }) }),
  getSubscribers: () => apiFetch('/newsletter/subscribers'),
  delete: (id) =>
    apiFetch(`/newsletter/${id}`, { method: 'DELETE' })
};

// ==================== COMMENTS ====================
export const commentAPI = {
  add: (commentData) =>
    apiFetch('/comments', { method: 'POST', body: JSON.stringify(commentData) }),
  getByPost: (postId) => apiFetch(`/comments/post/${postId}`),
  getAll: () => apiFetch('/comments'),
  update: (id, data) =>
    apiFetch(`/comments/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id) =>
    apiFetch(`/comments/${id}`, { method: 'DELETE' })
};

// ==================== PORTFOLIO ====================
export const portfolioAPI = {
  getAll: () => apiFetch('/portfolio'),
  create: (data) =>
    apiFetch('/portfolio', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) =>
    apiFetch(`/portfolio/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) =>
    apiFetch(`/portfolio/${id}`, { method: 'DELETE' })
};

// ==================== TESTIMONIALS ====================
export const testimonialAPI = {
  getApproved: () => apiFetch('/testimonials'),
  submit: (data) =>
    apiFetch('/testimonials', { method: 'POST', body: JSON.stringify(data) }),
  getAll: () => apiFetch('/testimonials/admin/all'),
  update: (id, data) =>
    apiFetch(`/testimonials/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id) =>
    apiFetch(`/testimonials/${id}`, { method: 'DELETE' })
};

// ==================== TEAM ====================
export const teamAPI = {
  getActive: () => apiFetch('/team'),
  getAll: () => apiFetch('/team/admin/all'),
  create: (data) =>
    apiFetch('/team', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) =>
    apiFetch(`/team/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) =>
    apiFetch(`/team/${id}`, { method: 'DELETE' })
};

// ==================== CAMPAIGNS ====================
export const campaignAPI = {
  getAll: () => apiFetch('/campaigns'),
  create: (data) =>
    apiFetch('/campaigns', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) =>
    apiFetch(`/campaigns/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) =>
    apiFetch(`/campaigns/${id}`, { method: 'DELETE' }),
  send: (id, htmlContent) =>
    apiFetch(`/campaigns/${id}/send`, { method: 'POST', body: JSON.stringify({ htmlContent }) })
};

// ==================== ANALYTICS ====================
export const analyticsAPI = {
  getDashboard: () => apiFetch('/analytics/dashboard')
};
